<template>
  <Teleport to="body">
    <div v-if="isOpen">
      <!-- Dimmed Backdrop -->
      <div
        class="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-[2px] transition-opacity animate-in fade-in duration-200"
        @click="close"
      ></div>

      <!-- Modal Bottom Sheet / Popover -->
      <div
        class="fixed z-[9999] bg-white text-fg border border-line shadow-2xl overflow-y-auto max-h-[90vh] md:max-h-[calc(100vh-120px)] inset-x-0 bottom-0 rounded-t-[24px] md:rounded-[20px] p-5 md:p-6 md:left-auto md:right-6 md:bottom-24 md:w-[380px] md:max-w-none animate-in slide-in-from-bottom duration-300 md:animate-pop-in"
        role="dialog"
        aria-modal="true"
        aria-labelledby="floating-form-title"
        data-testid="consult-modal"
      >
        <!-- Drag handle bar for Mobile bottom sheet -->
        <div class="md:hidden w-12 h-1 rounded-full bg-gray-300 mx-auto -mt-1 mb-4"></div>

        <!-- Header -->
        <div class="flex items-start justify-between mb-4">
          <div>
            <p id="floating-form-title" class="text-base font-bold text-fg mb-0.5">1:1 무료 상담 신청하기</p>
            <p class="text-xs text-fg-muted leading-snug">검토 후, 사장님 비즈니스에 맞는 담당자가 연락드립니다</p>
          </div>
          <button
            type="button"
            data-testid="button-close-form"
            aria-label="닫기"
            class="-mr-1 -mt-1 p-1.5 rounded-full text-fg-subtle hover:bg-bg-soft hover:text-fg transition-colors cursor-pointer"
            @click="close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-x">
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        <!-- Submission Success State -->
        <div v-if="isSubmitted" class="py-8 text-center space-y-3">
          <div class="w-12 h-12 rounded-full bg-brand-50 text-brand-500 flex items-center justify-center mx-auto">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-check">
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </div>
          <h4 class="text-lg font-bold text-fg">상담 신청이 완료되었습니다</h4>
          <p class="text-xs text-fg-muted leading-relaxed">입력해주신 정보를 바탕으로 빠른 시일 내 담당 매니저가 연락드리겠습니다.</p>
          <button type="button" class="mt-4 px-6 py-2.5 rounded-full bg-brand-500 text-white font-bold text-xs hover:bg-brand-600 transition-colors cursor-pointer" @click="close">확인</button>
        </div>

        <!-- Form Content -->
        <div v-else>
          <!-- Step Indicator -->
          <div class="flex items-center justify-between gap-2 mb-6">
            <div v-for="step in [1, 2, 3]" :key="step" class="flex-1 flex items-center gap-2">
              <div
                class="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all shrink-0"
                :class="
                  modalStep === step
                    ? 'bg-brand-500 text-white ring-2 ring-brand-200'
                    : modalStep > step
                    ? 'bg-brand-500 text-white'
                    : 'bg-white text-gray-400 border border-gray-200'
                "
              >
                <span v-if="modalStep > step">✓</span>
                <span v-else-if="modalStep === step" class="w-2.5 h-2.5 rounded-full bg-white"></span>
                <span v-else>{{ step }}</span>
              </div>
              <div
                v-if="step < 3"
                class="flex-1 h-0.5 rounded-full transition-colors"
                :class="modalStep > step ? 'bg-brand-500' : 'bg-gray-200'"
              ></div>
            </div>
          </div>

          <!-- Step 1 -->
          <div v-if="modalStep === 1" class="animate-in fade-in slide-in-from-right-6 duration-300">
            <div class="space-y-4" data-testid="step-segment">
              <div>
                <p class="text-sm font-bold text-fg">맞춤 상담</p>
                <p class="text-xs text-fg-muted mt-0.5">두 가지만 골라주시면 맞춤 안내가 빨라집니다. (선택)</p>
              </div>

              <div class="space-y-3">
                <div class="flex items-center gap-2">
                  <span class="shrink-0 w-16 text-xs font-medium text-fg-muted">상담 유형</span>
                  <div class="flex flex-wrap gap-1.5">
                    <button
                      type="button"
                      data-testid="form-consult-type-custom"
                      :aria-pressed="modalForm.consultType === '맞춤형 상담'"
                      class="px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer"
                      :class="modalForm.consultType === '맞춤형 상담' ? 'bg-brand-500 text-white border border-brand-500' : 'bg-white text-fg-muted hover:text-brand-600'"
                      :style="modalForm.consultType === '맞춤형 상담' ? {} : { border: '1px solid #f2b22a' }"
                      @click="modalForm.consultType = '맞춤형 상담'"
                    >
                      맞춤형 상담
                    </button>
                    <button
                      type="button"
                      data-testid="form-consult-type-allinone"
                      :aria-pressed="modalForm.consultType === '올인원 패키지'"
                      class="px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer"
                      :class="modalForm.consultType === '올인원 패키지' ? 'bg-brand-500 text-white border border-brand-500' : 'bg-white text-fg-muted hover:text-brand-600'"
                      :style="modalForm.consultType === '올인원 패키지' ? {} : { border: '1px solid #f2b22a' }"
                      @click="modalForm.consultType = '올인원 패키지'"
                    >
                      올인원 패키지
                    </button>
                  </div>
                </div>

                <div class="flex items-center gap-2">
                  <span class="shrink-0 w-16 text-xs font-medium text-fg-muted">서비스 형태</span>
                  <div class="flex flex-wrap gap-1.5">
                    <button
                      type="button"
                      data-testid="form-service-form-delivery"
                      :aria-pressed="modalForm.serviceType === '상품 배송형'"
                      class="px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer"
                      :class="modalForm.serviceType === '상품 배송형' ? 'bg-brand-500 text-white border border-brand-500' : 'bg-white text-fg-muted hover:text-brand-600'"
                      :style="modalForm.serviceType === '상품 배송형' ? {} : { border: '1px solid #f2b22a' }"
                      @click="modalForm.serviceType = '상품 배송형'"
                    >
                      상품 배송형
                    </button>
                    <button
                      type="button"
                      data-testid="form-service-form-visit"
                      :aria-pressed="modalForm.serviceType === '매장 방문형'"
                      class="px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer"
                      :class="modalForm.serviceType === '매장 방문형' ? 'bg-brand-500 text-white border border-brand-500' : 'bg-white text-fg-muted hover:text-brand-600'"
                      :style="modalForm.serviceType === '매장 방문형' ? {} : { border: '1px solid #f2b22a' }"
                      @click="modalForm.serviceType = '매장 방문형'"
                    >
                      매장 방문형
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="mt-6 flex items-center justify-end">
              <button
                type="button"
                data-testid="button-step-primary"
                class="px-7 py-3 rounded-full bg-brand-500 text-white font-bold text-sm hover:bg-brand-600 transition-colors cursor-pointer shadow-sm"
                @click="modalStep = 2"
              >
                다음
              </button>
            </div>
          </div>

          <!-- Step 2 -->
          <div v-else-if="modalStep === 2" class="animate-in fade-in slide-in-from-right-6 duration-300">
            <div class="space-y-3" data-testid="step-segment">
              <div>
                <p class="text-sm font-bold text-fg">기본 정보</p>
                <p class="text-xs text-fg-muted mt-0.5">연락을 받으실 정보만 간단히 적어주세요.</p>
              </div>

              <div class="space-y-3 pt-1">
                <div>
                  <label class="block text-xs font-medium text-fg mb-1">상호 / 브랜드명 <span class="text-brand-500">*</span></label>
                  <input
                    type="text"
                    v-model="modalForm.brand"
                    placeholder="예. 주식회사 티블"
                    class="w-full px-3.5 py-2.5 rounded-xl text-xs text-fg focus:outline-none focus:border-brand-500 bg-white placeholder-[#6b7280]"
                    style="border: 1px solid #ccc;"
                  />
                </div>
                <div>
                  <label class="block text-xs font-medium text-fg mb-1">연락처 <span class="text-brand-500">*</span></label>
                  <input
                    type="tel"
                    v-model="modalForm.phone"
                    placeholder="010-1234-5678"
                    class="w-full px-3.5 py-2.5 rounded-xl text-xs text-fg focus:outline-none focus:border-brand-500 bg-white placeholder-[#6b7280]"
                    style="border: 1px solid #ccc;"
                  />
                </div>
              </div>
            </div>

            <div class="mt-6 flex items-center justify-between gap-2">
              <button
                type="button"
                class="px-5 py-2.5 rounded-full border border-line text-fg-muted font-bold text-xs hover:bg-bg-soft transition-colors cursor-pointer"
                @click="modalStep = 1"
              >
                이전
              </button>
              <button
                type="button"
                data-testid="button-step-primary"
                class="px-7 py-3 rounded-full bg-brand-500 text-white font-bold text-sm hover:bg-brand-600 transition-colors cursor-pointer shadow-sm"
                @click="modalStep = 3"
              >
                다음
              </button>
            </div>
          </div>

          <!-- Step 3 -->
          <div v-else-if="modalStep === 3" class="animate-in fade-in slide-in-from-right-6 duration-300">
            <div class="space-y-3" data-testid="step-segment">
              <div>
                <p class="text-sm font-bold text-fg">상세 내용 (선택)</p>
              </div>

              <div class="space-y-3">
                <div>
                  <label class="block text-xs font-semibold text-fg mb-1">마케팅 지역 또는 제품 URL</label>
                  <input
                    type="text"
                    v-model="modalForm.locationOrUrl"
                    placeholder="예) 서울 강남 / smartstore.naver.com/..."
                    class="w-full px-3.5 py-2.5 rounded-xl text-xs text-fg bg-white focus:outline-none focus:border-brand-500 placeholder-[#6b7280]"
                    style="border: 1px solid #ccc;"
                  />
                </div>
                <div>
                  <label class="block text-xs font-semibold text-fg mb-1">문의 내용</label>
                  <textarea
                    v-model="modalForm.content"
                    rows="3"
                    placeholder="현재 매출/리뷰 상황, 목표를 알려주시면 더 정확하게 안내드려요"
                    class="w-full px-3.5 py-2.5 rounded-xl text-xs text-fg bg-white focus:outline-none focus:border-brand-500 placeholder-[#6b7280]"
                    style="border: 1px solid #ccc;"
                  ></textarea>
                </div>
                <div class="flex items-center gap-2 pt-1">
                  <input
                    type="checkbox"
                    id="modal-consent"
                    v-model="modalForm.agree"
                    class="w-4 h-4 rounded accent-brand-500 cursor-pointer"
                  />
                  <label for="modal-consent" class="text-xs text-fg cursor-pointer font-normal">개인정보 수집·활용에 동의합니다</label>
                </div>
              </div>
            </div>

            <div class="mt-6 flex items-center justify-between gap-2">
              <button
                type="button"
                class="px-4 py-2.5 rounded-full border border-line text-fg-muted font-bold text-xs hover:bg-bg-soft transition-colors cursor-pointer"
                @click="modalStep = 2"
              >
                이전
              </button>

              <div class="flex items-center gap-2">
                <a
                  href="https://pf.kakao.com"
                  target="_blank"
                  data-testid="button-modal-kakao"
                  class="w-9 h-9 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center hover:bg-brand-100 transition-colors"
                  title="카카오톡 문의"
                >
                  <img src="/images/kakao.svg" alt="카카오" class="w-4 h-4" />
                </a>
                <!-- <a
                  href="tel:1833-5985"
                  data-testid="button-modal-phone"
                  class="w-9 h-9 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center hover:bg-brand-100 transition-colors"
                  title="전화 문의"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-phone">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                </a> -->
                <button
                  type="button"
                  data-testid="button-step-primary"
                  :disabled="!modalForm.brand || !modalForm.phone || !modalForm.agree || isSubmitting"
                  class="px-5 py-2.5 rounded-full bg-brand-500 text-white font-bold text-xs hover:bg-brand-600 disabled:opacity-50 transition-colors cursor-pointer border border-brand-600 flex items-center justify-center gap-1.5"
                  @click="submitForm"
                >
                  <span v-if="isSubmitting" class="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  <span>{{ isSubmitting ? '신청 중...' : '무료 상담 신청하기' }}</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Bottom Notice Footer -->
          <div class="mt-5 pt-3.5 border-t border-line/70 text-center">
            <p class="text-[11px] text-fg-muted">영업일 기준 24시간 내로 회신드립니다.</p>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
/* global defineProps, defineEmits */
import { ref, reactive, watch } from 'vue';

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close']);

const modalStep = ref(1);
const isSubmitted = ref(false);
const isSubmitting = ref(false);

const modalForm = reactive({
  consultType: '맞춤형 상담',
  serviceType: '상품 배송형',
  brand: '',
  phone: '',
  locationOrUrl: '',
  content: '',
  agree: false,
});

watch(
  () => props.isOpen,
  (newVal) => {
    if (newVal) {
      modalStep.value = 1;
      isSubmitted.value = false;
      isSubmitting.value = false;
    }
  }
);

const close = () => {
  emit('close');
};

const sendInboundSlackNotification = (data) => {
  const envUrl = process.env.VUE_APP_SLACK_WEBHOOK_URL;
  const slackWebhookUrl =
    envUrl && !envUrl.includes('YOUR/3ALLIANCE/WEBHOOK') && !envUrl.includes('VNGN7xSHEsulq3N6UkbxN5jJ')
      ? envUrl
      : 'https://hooks.slack.com/services/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';

  if (!slackWebhookUrl || slackWebhookUrl.includes('YOUR/3ALLIANCE/WEBHOOK')) {
    console.warn('[Slack] 웹훅 URL이 설정되지 않았습니다.');
    return;
  }

  const payload = {
    text: `🚨 [티블비즈] 신규 인바운드 접수: ${data.customerName || '미입력'}`,
    blocks: [
      {
        type: 'header',
        text: {
          type: 'plain_text',
          text: '🚨 [티블비즈] 신규 인바운드 접수 알림',
          emoji: true,
        },
      },
      {
        type: 'section',
        fields: [
          { type: 'mrkdwn', text: `*고객명(브랜드):*\n${data.customerName || '-'}` },
          { type: 'mrkdwn', text: `*연락처:*\n${data.phone || '-'}` },
          { type: 'mrkdwn', text: `*상담/서비스 유형:*\n${(data.consultType || '-') + ' / ' + (data.serviceType || '-')}` },
          { type: 'mrkdwn', text: `*접수시각:*\n${data.createdAt || new Date().toLocaleString('ko-KR')}` },
        ],
      },
      {
        type: 'section',
        fields: [
          { type: 'mrkdwn', text: `*지역 또는 URL:*\n${data.locationOrUrl || '-'}` },
          { type: 'mrkdwn', text: `*이메일:*\n${data.email || '-'}` },
        ],
      },
      {
        type: 'section',
        text: {
          type: 'mrkdwn',
          text: `*문의내용:*\n${data.message || '내용 없음'}`,
        },
      },
    ],
  };

  try {
    // 브라우저 CORS 차단 방지를 위해 no-cors 및 text/plain 모드로 전송
    fetch(slackWebhookUrl, {
      method: 'POST',
      mode: 'no-cors',
      keepalive: true,
      headers: {
        'Content-Type': 'text/plain',
      },
      body: JSON.stringify(payload),
    })
      .then(() => {
        console.log('[Slack] 3alliance 채널 알림 전송 요청 완료');
      })
      .catch((error) => {
        console.error('[Slack] 알림 전송 실패:', error);
      });
  } catch (error) {
    console.error('[Slack] 전송 준비 중 오류 발생:', error);
  }
};

const submitForm = () => {
  if (!modalForm.brand || !modalForm.phone || !modalForm.agree || isSubmitting.value) return;

  isSubmitting.value = true;

  const webhookUrl =
    process.env.VUE_APP_GOOGLE_SHEET_WEBAPP_URL ||
    'https://script.google.com/macros/s/AKfycbwoIkSHqb1icKR9CRlVWXUYWnonH1fIDG0P9y7h2AhWd5RUlnmu-SiOQIx7usaBpNiQtg/exec';

  const now = new Date().toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' });

  // 1. 구글 스프레드시트로 전송
  if (webhookUrl) {
    try {
      const formData = new URLSearchParams();
      formData.append('createdAt', now);
      formData.append('consultType', modalForm.consultType || '');
      formData.append('serviceType', modalForm.serviceType || '');
      formData.append('brand', modalForm.brand || '');
      formData.append('phone', modalForm.phone || '');
      formData.append('locationOrUrl', modalForm.locationOrUrl || '');
      formData.append('content', modalForm.content || '');

      // 구글 앱스 스크립트 특유의 302 리다이렉트 응답 대기 지연을 방지하기 위해 백그라운드 전송
      fetch(webhookUrl, {
        method: 'POST',
        mode: 'no-cors',
        keepalive: true,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      }).catch((error) => {
        console.error('구글 시트 연동 전송 중 오류 발생:', error);
      });
    } catch (error) {
      console.error('구글 시트 연동 전송 준비 중 오류 발생:', error);
    }
  }

  // 2. 구글 시트 전송과 함께 슬랙 3alliance 채널 알림 발송
  sendInboundSlackNotification({
    customerName: modalForm.brand,
    phone: modalForm.phone,
    consultType: modalForm.consultType,
    serviceType: modalForm.serviceType,
    locationOrUrl: modalForm.locationOrUrl,
    message: modalForm.content,
    createdAt: now,
  });

  // 3. GA4 / GTM 전환 이벤트 전송 (개인정보는 절대 전송하지 않음)
  if (typeof window !== 'undefined' && typeof window.trackConsultSubmitSuccess === 'function') {
    window.trackConsultSubmitSuccess();
  }

  isSubmitting.value = false;
  isSubmitted.value = true;
};
</script>

<style scoped>
input::placeholder,
textarea::placeholder {
  color: #6b7280 !important;
  opacity: 1 !important;
}
</style>
